import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Building2,
  FolderKanban,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { ProjectCostingSummary } from '../types/index.ts';

interface ProjectCostingPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const ProjectCostingPage: React.FC<ProjectCostingPageProps> = ({ onNavigate }) => {
  const [costings, setCostings] = useState<ProjectCostingSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCosting = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/costing');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) setCostings(data);
      }
    } catch (err) {
      console.warn('Costing fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCosting();
  }, []);

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  const safeCostings = Array.isArray(costings) ? costings : [];
  const totalContract = safeCostings.reduce((sum, c) => sum + (Number(c.contractValue) || 0), 0);
  const totalActual = safeCostings.reduce((sum, c) => sum + (Number(c.totalActual) || 0), 0);
  const totalProfit = safeCostings.reduce((sum, c) => sum + (Number(c.grossProfit) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Project Costing &amp; Profitability
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-200">
              Estimated vs. Actual
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Full job-costing breakdown: Materials, Labour, Transport &amp; Machinery vs. Client PO values.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate('reports')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <span>Financial Reports →</span>
          </button>
        </div>
      </div>

      {/* Aggregate KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Total Contract Values
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{formatPKR(totalContract)}</div>
          <p className="text-[11px] text-slate-400 mt-0.5">Across all branch projects</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Actual Direct Cost Incurred
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{formatPKR(totalActual)}</div>
          <p className="text-[11px] text-slate-400 mt-0.5">Materials + Labour + Logistics</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Cumulative Gross Profit
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{formatPKR(totalProfit)}</div>
          <p className="text-[11px] text-emerald-800 font-semibold mt-0.5">
            ~{totalContract > 0 ? Math.round((totalProfit / totalContract) * 100) : 0}% Realized Margin
          </p>
        </div>
      </div>

      {/* Project-by-Project Costing Breakdown */}
      <div className="space-y-4">
        {safeCostings.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-blue-900">{c.projectNumber}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    {Math.round(c.marginPercent)}% Margin
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-slate-800 mt-0.5">{c.projectName}</h3>
              </div>

              <div className="text-right">
                <div className="text-xs text-slate-400">Gross Margin</div>
                <div className="text-base font-bold text-emerald-700">{formatPKR(c.grossProfit)}</div>
              </div>
            </div>

            {/* Cost Categories Grid: Material, Labour, Transport, Other */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                <span className="text-[10px] text-slate-400 font-bold uppercase block">1. Material Cost</span>
                <span className="text-sm font-bold text-slate-900 mt-1 block">
                  {formatPKR(c.materialCost)}
                </span>
                <span className="text-[11px] text-slate-500">Cement, tiles, paints</span>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                <span className="text-[10px] text-slate-400 font-bold uppercase block">2. Labour Cost</span>
                <span className="text-sm font-bold text-slate-900 mt-1 block">{formatPKR(c.labourCost)}</span>
                <span className="text-[11px] text-slate-500">Masons, carpenters, helpers</span>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                <span className="text-[10px] text-slate-400 font-bold uppercase block">3. Transport &amp; Fuel</span>
                <span className="text-sm font-bold text-slate-900 mt-1 block">
                  {formatPKR(c.transportCost)}
                </span>
                <span className="text-[11px] text-slate-500">Mazda truck &amp; site vehicle</span>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                <span className="text-[10px] text-slate-400 font-bold uppercase block">4. Other Site Exp.</span>
                <span className="text-sm font-bold text-slate-900 mt-1 block">{formatPKR(c.otherCost)}</span>
                <span className="text-[11px] text-slate-500">Hardware, safety, tools</span>
              </div>
            </div>

            {/* Progress bar vs Contract Value */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">
                  Total Spent: <strong className="text-slate-800">{formatPKR(c.totalActual)}</strong>
                </span>
                <span className="text-slate-500">
                  Contract: <strong className="text-slate-800">{formatPKR(c.contractValue)}</strong>
                </span>
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-blue-600 h-full rounded-full"
                  style={{
                    width: `${Math.min(100, (c.totalActual / (c.contractValue || 1)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
